import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserAuthError } from '@azure/msal-browser';
import { App } from './App';
import { msalInstance, redirectPromiseOptions } from './auth/msalConfig';
import { markSignInFailed } from './auth/authStatus';
import './i18n/config';

/**
 * MSAL must complete `initialize()` before any component subscribes to its
 * events - otherwise `useIsAuthenticated` can resolve against an in-flight
 * cache state. We also drain any pending redirect response here so the user
 * lands on the originally-requested URL after sign-in completes.
 *
 * INVARIANT — do not move `handleRedirectPromise()` after `render()`.
 * `VITE_ENTRA_REDIRECT_URI` is `/`, and `/` is the index route *inside*
 * `RequireAuth`, which since DDR-PLT-024 redirects an unauthenticated caller
 * straight to Microsoft. Draining the redirect response before the first render
 * is the only reason that is not an infinite loop: it caches the account (and,
 * on failure, sets `signInFailed`) before any guard evaluates. Rendering first
 * would send every returning user back to Entra forever.
 *
 * `handleRedirectPromise` can reject with `no_token_request_cache_error` when
 * it is invoked without a redirect response to process against a stored
 * request (MSAL Browser 5.x). We swallow only that code and fall through to
 * the account check below: if a session is already cached the app proceeds
 * authenticated; if not, `RequireAuth` starts a fresh sign-in. Either way it is
 * not a hard failure worth a dead-end card.
 *
 * Any OTHER rejection means the interactive redirect genuinely failed — the
 * user cancelled it, it timed out, or Entra/config returned an error. Rather
 * than crash the bootstrap (white screen), flag `signInFailed` and let the app
 * render. That flag is what stops the guard bouncing the user straight back to
 * Microsoft: `RequireAuth` short-circuits on it and shows the A3 "sign-in
 * didn't complete" card with a working "Try again". The copy is
 * deliberately cause-agnostic (it does not assert cancellation) because this
 * branch also covers config/network failures; the underlying error is logged
 * for operators. A distinct operator-facing config-error surface is a future
 * nicety (deploy-time misconfig is not client-recoverable regardless).
 */
async function bootstrap() {
  await msalInstance.initialize();

  try {
    await msalInstance.handleRedirectPromise(redirectPromiseOptions);
  } catch (error) {
    if (error instanceof BrowserAuthError && error.errorCode === 'no_token_request_cache_error') {
      // No redirect response to drain - benign; fall through to the account check.
    } else {
      console.error('MSAL interactive sign-in did not complete', error);
      markSignInFailed();
    }
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0] ?? null);
  }

  // Dev-only mock seam. The dynamic import keeps MSW out of a real
  // (flag-off) bundle; the worker intercepts fixture-backed endpoints so the
  // SPA runs without a live API. Backed screens flip to real by unsetting the
  // flag — the query hooks are identical in both modes.
  if (import.meta.env.VITE_ENABLE_MOCKS === 'true') {
    await (await import('./mocks/enableMocks')).enableMocks();
  }

  const container = document.getElementById('root');
  if (!container) {
    throw new Error('Root container missing in index.html');
  }

  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

void bootstrap();
